import { useEffect, useState, type FormEvent } from 'react';
import { Check, Loader2, Save } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, type Organization } from '../lib/admin-api';
import { Button, Input, Label, Textarea, Switch, PageHeading, SettingSection } from '../components/admin/ui';

type Settings = Record<string, unknown>;
function group(settings: Settings, key: string): Settings {
  const value = settings[key];
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Settings) : {};
}
function string(value: unknown): string {
  return typeof value === 'string' ? value : '';
}
function number(value: unknown): number | '' {
  return typeof value === 'number' && Number.isFinite(value) ? value : '';
}

export function SettingsPage() {
  const { profile } = useAuth();
  return (
    <OrganizationSettings
      key={profile?.organization_id ?? 'unassigned'}
      organizationId={profile?.organization_id}
    />
  );
}

// A workspace switch creates a fresh form; an old save cannot replace its snapshot.
function OrganizationSettings({ organizationId }: { organizationId: string | null | undefined }) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [baseline, setBaseline] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSettings(null);
    setOrganization(null);
    setError('');
    setSaved(false);
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void adminApi
      .getOrganization(organizationId)
      .then((org) => {
        if (cancelled) return;
        if (!org) {
          setError('We couldn’t load your workspace settings. Try again.');
          return;
        }
        const value =
          org.settings && typeof org.settings === 'object' && !Array.isArray(org.settings)
            ? org.settings
            : {};
        setOrganization(org);
        setSettings(value);
        setBaseline(JSON.stringify(value));
      })
      .catch(() => {
        if (!cancelled) setError('We couldn’t load your workspace settings. Try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, attempt]);

  const dirty = settings !== null && JSON.stringify(settings) !== baseline;
  function update(section: string, key: string, value: unknown): void {
    setSettings((current) =>
      current ? { ...current, [section]: { ...group(current, section), [key]: value } } : current,
    );
    setSaved(false);
    setError('');
  }
  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!organization || !settings || saving || !dirty) return;
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const success = await adminApi.updateOrganization(organization.id, { settings });
      if (!success) throw new Error('Save failed');
      setBaseline(JSON.stringify(settings));
      setSaved(true);
    } catch {
      setError('Changes weren’t saved. Your edits are still here. Try saving again.');
    } finally {
      setSaving(false);
    }
  }

  const branding = group(settings || {}, 'branding');
  const ai = group(settings || {}, 'ai');
  const features = group(settings || {}, 'features');
  const threshold = number(ai.confidence_threshold);

  return (
    <div className="support-page max-w-5xl">
      <PageHeading title="Settings" description="Make this workspace work for your support team." />
      {loading ? (
        <div role="status" className="support-panel flex items-center gap-3 p-8">
          <Loader2 className="h-5 w-5 animate-spin text-link" />
          Loading workspace settings…
        </div>
      ) : !settings ? (
        <div className="support-feedback" role="alert">
          <p>
            {!organizationId
              ? 'Your account has no workspace assigned. Ask your administrator to assign one.'
              : error}
          </p>
          {organizationId && (
            <Button variant="outline" className="mt-4" onClick={() => setAttempt((value) => value + 1)}>
              Try again
            </Button>
          )}
        </div>
      ) : (
        <form onSubmit={(event) => void save(event)}>
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <nav
              aria-label="Settings sections"
              className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-muted-foreground"
            >
              {['Workspace', 'Branding', 'AI responses', 'Attachments'].map((label, index) => (
                <a
                  key={label}
                  className="hover:text-link"
                  href={`#${['workspace', 'branding', 'ai', 'attachments'][index]}`}
                >
                  {label}
                </a>
              ))}
            </nav>
            <span className="support-label">Organization settings</span>
          </div>
          <fieldset disabled={saving} className="min-w-0">
            <SettingSection
              id="workspace"
              title="Workspace"
              description="Your organization’s identity. Contact your administrator to change these details."
            >
              <dl className="support-panel divide-y divide-border px-5">
                {[
                  ['Name', organization?.name],
                  ['Workspace URL', organization?.slug],
                  ['Plan', organization?.plan],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-wrap items-center justify-between gap-3 py-4">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="break-all font-medium">{value || 'Not configured'}</dd>
                  </div>
                ))}
              </dl>
            </SettingSection>
            <SettingSection
              id="branding"
              title="Branding"
              description="Choose the color and logo used by your customer-facing support experience."
            >
              <div className="support-field">
                <Label htmlFor="primary-color">Primary color</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    aria-label="Choose primary color"
                    className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-border bg-background p-1"
                    value={
                      /^#[0-9a-f]{6}$/i.test(string(branding.primary_color))
                        ? string(branding.primary_color)
                        : '#5EA4BE'
                    }
                    onChange={(event) => update('branding', 'primary_color', event.target.value)}
                  />
                  <Input
                    id="primary-color"
                    className="max-w-48 font-mono"
                    value={string(branding.primary_color)}
                    placeholder="Not configured"
                    required={branding.primary_color != null}
                    pattern="#[0-9a-fA-F]{6}"
                    title="Enter a six-digit hex color, such as #5EA4BE"
                    onChange={(event) => update('branding', 'primary_color', event.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">A six-digit hex color, including the #.</p>
              </div>
              <div className="support-field">
                <Label htmlFor="logo-url">Logo URL</Label>
                <Input
                  id="logo-url"
                  type="url"
                  value={string(branding.logo_url)}
                  placeholder="https://example.com/logo.svg"
                  onChange={(event) => update('branding', 'logo_url', event.target.value || null)}
                />
                <p className="text-xs text-muted-foreground">Use a publicly accessible image URL.</p>
              </div>
            </SettingSection>
            <SettingSection
              id="ai"
              title="AI responses"
              description="Control how AI greets customers and resolves conversations."
            >
              <Switch
                id="ai-enabled"
                label="Enable AI responses"
                description={
                  typeof features.ai_enabled === 'boolean'
                    ? 'Allow AI to respond to customer conversations.'
                    : 'Not configured. Changing this control sets an explicit preference.'
                }
                checked={features.ai_enabled === true}
                onCheckedChange={(value) => update('features', 'ai_enabled', value)}
              />
              <div className="border-t border-border pt-5">
                <Switch
                  id="auto-resolve"
                  label="Automatically resolve conversations"
                  description={
                    typeof ai.auto_resolve_enabled === 'boolean'
                      ? 'Allow AI to resolve simple queries.'
                      : 'Not configured. Changing this control sets an explicit preference.'
                  }
                  checked={ai.auto_resolve_enabled === true}
                  onCheckedChange={(value) => update('ai', 'auto_resolve_enabled', value)}
                />
              </div>
              <div className="support-field">
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="confidence">Confidence threshold</Label>
                  <output htmlFor="confidence" className="font-mono text-sm text-link">
                    {threshold === '' ? 'Not configured' : `${Math.round(threshold * 100)}%`}
                  </output>
                </div>
                <Input
                  id="confidence"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  className="max-w-28 font-mono"
                  value={threshold === '' ? '' : Math.round(threshold * 100)}
                  onChange={(event) =>
                    update(
                      'ai',
                      'confidence_threshold',
                      event.target.value === '' ? '' : Number(event.target.value) / 100,
                    )
                  }
                  required={ai.confidence_threshold != null}
                  aria-describedby="confidence-hint"
                />
                {threshold !== '' && (
                  <input
                    aria-label="Adjust confidence threshold"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={Math.round(threshold * 100)}
                    onChange={(event) =>
                      update('ai', 'confidence_threshold', Number(event.target.value) / 100)
                    }
                    className="w-full"
                  />
                )}
                <p id="confidence-hint" className="text-xs text-muted-foreground">
                  0–100%. Higher values require more confident AI responses.
                </p>
              </div>
              <div className="support-field">
                <Label htmlFor="greeting">Greeting message</Label>
                <Textarea
                  id="greeting"
                  rows={3}
                  value={string(ai.greeting_message)}
                  onChange={(event) => update('ai', 'greeting_message', event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  The message customers see when they start a conversation.
                </p>
              </div>
            </SettingSection>
            <SettingSection
              id="attachments"
              title="Attachments"
              description="Set whether customers can attach files to a conversation."
            >
              <Switch
                id="uploads"
                label="Allow file attachments"
                description={
                  typeof features.file_uploads === 'boolean'
                    ? 'Let customers share files with your team.'
                    : 'Not configured. Changing this control sets an explicit preference.'
                }
                checked={features.file_uploads === true}
                onCheckedChange={(value) => update('features', 'file_uploads', value)}
              />
              <div className="support-field">
                <Label htmlFor="file-size">Maximum file size</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="file-size"
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    className="max-w-28 font-mono"
                    value={number(features.max_file_size_mb)}
                    onChange={(event) =>
                      update(
                        'features',
                        'max_file_size_mb',
                        event.target.value === '' ? '' : Number(event.target.value),
                      )
                    }
                    required={features.max_file_size_mb != null}
                  />
                  <span className="text-sm text-muted-foreground">MB</span>
                </div>
                <p className="text-xs text-muted-foreground">Between 1 and 100 MB per file.</p>
              </div>
            </SettingSection>
          </fieldset>
          <div className="sticky bottom-0 -mx-4 border-t border-border bg-background px-4 py-4 sm:mx-0">
            {error && (
              <p role="alert" className="mb-3 text-sm text-error-text">
                {error}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                {saved && <Check className="h-4 w-4" />}
                {saving
                  ? 'Saving your changes…'
                  : saved
                    ? 'Changes saved'
                    : dirty
                      ? 'You have unsaved changes'
                      : 'No unsaved changes'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={!dirty || saving}
                  onClick={() => {
                    setSettings(JSON.parse(baseline));
                    setError('');
                    setSaved(false);
                  }}
                >
                  Discard
                </Button>
                <Button type="submit" disabled={!dirty || saving}>
                  {saving ? <Loader2 className="animate-spin" /> : <Save />}Save changes
                </Button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
